'use client';

import { useEffect, useState } from 'react';

import PromotionContent from './PromotionContent';
import AvailabilityPricePreview from './PricePreview';
import ExtrasAndPriceIncludedContent from './ExtrasContent';
import { getFilteredDeals, getPriceIncludesData } from './utils';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { Village, VillageFutureBooking } from '@/services/types';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { IExtraQuantitySelectDictionary } from '@/dictionaries/components/extraQuantitySelect/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';

import { cn } from '@/lib/utils';
import Slider, { Settings } from 'react-slick';
import { getVillageFutureBookings } from '@/actions/villages';
import { Loading } from '../Loading';

type Dictionaries = {
  datePickerDict: IDatePickerDictonary;
  enquiryDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  modalsDict: IModalsDictionary;
  villageDict: IVillageDictionary;
  extraQuantitySelectDict: IExtraQuantitySelectDictionary;
};

type Props = {
  village: Village;
  dicts: Dictionaries;
  lang: string;
};

export default function AvailabilitySectionTabs({ village, dicts, lang }: Props) {
  const [futureBookings, setFutureBookings] =
    useState<VillageFutureBooking[]>();
  const [isLoading, setIsLoading] = useState(false);
  const { label: availabilityLabel, promo } = dicts.villageDict.availability;
  const [selectedSection, setSelectedSection] = useState(availabilityLabel);

  const { priceIncludesFeatures, priceIncludesNotes } = getPriceIncludesData(
    village,
    lang,
  );
  const filteredDeals = getFilteredDeals(village);
  const canShowPromos = filteredDeals.length > 0;
  const canShowCalendar = !isLoading && !!futureBookings;

  const topPricesSlide: Settings = {
    responsive: [
      {
        breakpoint: 9999,
        settings: 'unslick',
      },
      {
        breakpoint: 350,
        settings: {
          centerMode: true,
          slidesToShow: 1,
          arrows: false,
          dots: false,
          infinite: false,
          variableWidth: true,
        },
      },
    ],
  };

  const sections = canShowPromos
    ? [availabilityLabel, promo]
    : [availabilityLabel];

  useEffect(() => {
    let retryTimeout: NodeJS.Timeout | null = null;

    async function handleGetVillageBookingDates() {
      setIsLoading(true);
      try {
        const { data } = await getVillageFutureBookings(village?.slug || '');

        setFutureBookings(data);
      } catch (error) {
        setFutureBookings(village?.futureBookings || []);
      } finally {
        setIsLoading(false);
      }
    }

    if (!futureBookings && !isLoading) {
      handleGetVillageBookingDates();
    }

    if (isLoading) {
      retryTimeout = setTimeout(() => {
        if (isLoading) {
          handleGetVillageBookingDates();
        }
      }, 5000);
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [village, futureBookings, isLoading]);

  return (
    <div className="availability-section-tabs">
      <div className="w-full flex-[0_0_auto] mb-[30px]">
        <Slider {...topPricesSlide} className="flex flex-nowrap ">
          {sections.map((section) => (
            <div
              onClick={() => setSelectedSection(section)}
              role="presentation"
              key={section}
              className="float-left z-[50] px-6 first:!pl-0">
              <div className="bg-white text-graphite block py-5 text-xs leading-[1] cursor-pointer">
                <h3
                  className={cn(
                    'text-gray-500 m-0 text-center text-4xl font-title leading-[1.25] bg-gradient-to-b bg-no-repeat bg-[length:95%_0.2em] bg-[50%_75%] hover:text-graphite hover:from-underline hover:to-underline',
                    {
                      'px-[0.2em] mx-[-0.2em] text-graphite from-underline to-underline':
                        selectedSection === section,
                    },
                  )}>
                  {section}
                </h3>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      <div
        className={cn('text-graphite', {
          'w-full': isLoading,
        })}>
        {selectedSection === promo && (
          <PromotionContent lang={lang} deals={filteredDeals} />
        )}
        {selectedSection === availabilityLabel && (
          <>
            {!canShowCalendar ? (
              <div className="flex justify-center items-center py-12">
                <Loading />
              </div>
            ) : (
              <AvailabilityPricePreview
                dicts={dicts}
                lang={lang}
                village={village}
                futureBookings={futureBookings}
              />
            )}

            <ExtrasAndPriceIncludedContent
              dicts={{
                villageDict: dicts.villageDict,
                extraQuantitySelectDict: dicts.extraQuantitySelectDict,
              }}
              translatedFeatures={priceIncludesFeatures}
              featuresNotes={priceIncludesNotes}
              extras={village.extras}
              lang={lang}
            />
          </>
        )}
      </div>
    </div>
  );
}
